import type { IndividualInterface } from '@interfaces/Individual.interface';

/** Soft default when no entity / session brand color applies. */
export const CRM_AVATAR_SOFT_BG = '#E8EEF9';
export const CRM_AVATAR_SOFT_TEXT = '#2563EB';

export type AvatarBrandPresentation = {
  color: string;
  textColor?: string;
};

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const MAX_DEPTH = 3;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const prefixed = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (!HEX_COLOR_PATTERN.test(prefixed)) return null;
  return prefixed.length === 4
    ? `#${prefixed[1]}${prefixed[1]}${prefixed[2]}${prefixed[2]}${prefixed[3]}${prefixed[3]}`
    : prefixed;
}

function readObjectColor(value: unknown, depth: number): string | null {
  if (!isRecord(value) || depth > MAX_DEPTH) return null;

  const directKeys = [
    'main_color',
    'company_branding_colour',
    'company_brand_color',
    'brand_color',
    'color'
  ];

  for (const key of directKeys) {
    const resolved = normalizeHexColor(value[key]);
    if (resolved) return resolved;
  }

  const nestedKeys = [
    'brand',
    'company',
    'tenant',
    'owner',
    'user',
    'individual',
    'client',
    'company_branding'
  ];

  for (const key of nestedKeys) {
    const resolved = readObjectColor(value[key], depth + 1);
    if (resolved) return resolved;
  }

  return null;
}

export function resolveEntityBrandColor(
  ...candidates: unknown[]
): string | null {
  for (const candidate of candidates) {
    const normalized = normalizeHexColor(candidate);
    if (normalized) return normalized;

    const fromObject = readObjectColor(candidate, 0);
    if (fromObject) return fromObject;
  }
  return null;
}

function saturatedPresentation(hex: string): AvatarBrandPresentation {
  return { color: hex };
}

function softDefaultPresentation(): AvatarBrandPresentation {
  return {
    color: CRM_AVATAR_SOFT_BG,
    textColor: CRM_AVATAR_SOFT_TEXT
  };
}

export function entityCompanyAvatarProps(
  ...candidates: unknown[]
): AvatarBrandPresentation {
  const resolved = resolveEntityBrandColor(...candidates);
  if (resolved) return saturatedPresentation(resolved);
  return softDefaultPresentation();
}

export function getIndividualLinkedCompanyForAvatar(
  row: IndividualInterface | null | undefined
) {
  const relations = row?.company_relations;
  if (relations?.length)
    return relations.find((c) => c.is_primary) ?? relations[0];
  const companies = row?.companies;
  if (companies?.length)
    return companies.find((c) => c.is_primary) ?? companies[0];
  return undefined;
}

export function individualListAvatarProps(
  row: IndividualInterface | null | undefined
): AvatarBrandPresentation {
  const linked = getIndividualLinkedCompanyForAvatar(row);
  return entityCompanyAvatarProps(linked);
}

export function placeholderAvatarProps(): AvatarBrandPresentation {
  return softDefaultPresentation();
}

export function kanbanAssigneeAvatarProps(
  isDormant: boolean
): AvatarBrandPresentation {
  if (isDormant) return { color: '#98A2B3' };
  return placeholderAvatarProps();
}

export function userProfileLetterPresentation(
  sessionOrCompanyHex: string | null | undefined
): AvatarBrandPresentation {
  const normalized = normalizeHexColor(sessionOrCompanyHex);
  if (normalized) return saturatedPresentation(normalized);
  return softDefaultPresentation();
}

export function companyMenuLetterPresentation(
  sessionHex: string | null | undefined,
  ...queryCandidates: unknown[]
): AvatarBrandPresentation {
  const fromQuery = resolveEntityBrandColor(...queryCandidates);
  if (fromQuery) return saturatedPresentation(fromQuery);
  return userProfileLetterPresentation(sessionHex);
}
